import * as React from 'react'
import { ChevronDown, Loader2 } from 'lucide-react'
import { formatMoney } from '@lib/helpers/money'
import { cn } from '@/lib/utils'

/** How long a change is shown before the total comes back. */
const FLASH_MS = 2500

/** A total rounded to the dollar, for a glance: "$9,106". */
export function formatWholeDollars(cents: number): string {
  return formatMoney(Math.round(cents / 100) * 100).replace(/\.00$/, '')
}

/** A change in the total, signed: "+$180", "−$45", or cents under a dollar. */
export function formatChange(cents: number): string {
  const sign = cents < 0 ? '−' : '+'
  const size = Math.abs(cents)
  return `${sign}${size < 100 ? formatMoney(size) : formatWholeDollars(size)}`
}

export interface BidTotalButtonProps {
  /** The headline, tax included, or null when nothing can be priced. */
  totalCents: number | null
  /** The headline is a floor: some work has no time yet. */
  isIncomplete: boolean
  /**
   * The figure the technician's own edits move. A change in it flashes the
   * change in the headline; the headline moving for any other reason (the
   * bid's other sites arriving from the server) does not.
   */
  editedCents: number
  isLoading: boolean
  open: boolean
  onToggle: () => void
  /** Id of the tray it opens, for `aria-controls`. */
  controls: string
}

/**
 * The bid's total in the survey header, which opens the tray.
 *
 * Always showing the figure was the operator's call (2026-11-04): nobody
 * else is looking at the screen. After an edit it shows what that edit
 * changed for a moment, so the cost of a change is learned without opening
 * anything. A total with trees still missing a time ends in "+", as the
 * hours beside it do.
 */
export function BidTotalButton({
  totalCents,
  isIncomplete,
  editedCents,
  isLoading,
  open,
  onToggle,
  controls,
}: BidTotalButtonProps) {
  const [change, setChange] = React.useState<number | null>(null)
  const last = React.useRef<{ edited: number; total: number | null } | null>(
    null,
  )

  // Before paint, so the new total is never drawn for a frame ahead of the
  // change that replaces it.
  React.useLayoutEffect(() => {
    const previous = last.current
    last.current = { edited: editedCents, total: totalCents }
    if (
      previous === null ||
      previous.edited === editedCents ||
      previous.total === null ||
      totalCents === null ||
      previous.total === totalCents
    ) {
      return undefined
    }
    setChange(totalCents - previous.total)
    const timer = window.setTimeout(() => {
      setChange(null)
    }, FLASH_MS)
    return () => {
      window.clearTimeout(timer)
    }
  }, [editedCents, totalCents])

  function label(): string {
    if (change !== null) return formatChange(change)
    if (totalCents === null) return '$ —'
    return `${formatWholeDollars(totalCents)}${isIncomplete ? '+' : ''}`
  }

  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={controls}
      aria-label={
        totalCents === null
          ? 'Bid total: not priced yet'
          : `Bid total ${formatMoney(totalCents)}${isIncomplete ? ', not every tree has a time yet' : ''}`
      }
      onClick={onToggle}
      className={cn(
        'flex h-9 shrink-0 items-center gap-1 rounded-md border px-2 text-sm font-semibold tabular-nums transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none',
        open
          ? 'border-brand bg-brand-subtle text-primary-text'
          : 'border-default bg-surface text-primary-text hover:border-strong hover:bg-subtle',
        change !== null && 'border-brand bg-brand-subtle',
        isIncomplete && change === null && 'text-warning',
      )}
    >
      {isLoading && totalCents === null ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-text" />
      ) : null}
      <span aria-live="polite">{label()}</span>
      <ChevronDown
        className={cn(
          'h-3.5 w-3.5 text-muted-text transition-transform duration-200',
          open && 'rotate-180',
        )}
      />
    </button>
  )
}
